import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import ls from "localstorage-slim";
import { authPostRequest } from "../../api/authClient";
import { hasAppLoggedOutFlag, redirectToKeycloakLogin } from "../../auth/logout";
import { isAppSessionActive } from "../../auth/session/authGate";
import { useAuthMe } from "../../hooks/useAuthMe";
import { SenrysaLogo } from "../../Components/SenrysaLogo";
import { AuthTransitionScreen } from "../../Components/AuthTransitionScreen";

type LoginResponse = {
  access_token?: string;
  refresh_token?: string;
  expires_in?: string | number;
  token?: string;
  user?: unknown;
  success?: boolean;
  message?: string;
};

function getRedirectPath(locationState: unknown): string {
  const from = (locationState as { from?: { pathname?: string } } | null)?.from?.pathname;
  if (from && typeof from === "string" && from.startsWith("/")) return from;
  return "/";
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const me = useAuthMe({ redirectOn401: false });

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const redirectPath = useMemo(() => getRedirectPath(location.state), [location.state]);

  useEffect(() => {
    if (hasAppLoggedOutFlag()) {
      void redirectToKeycloakLogin();
    }
  }, []);

  useEffect(() => {
    if (isAppSessionActive() && me.data && !me.isError) {
      navigate("/", { replace: true });
    }
  }, [me.data, me.isError, navigate]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    setIsLoading(true);
    try {
      const res = await authPostRequest<LoginResponse>("/client/auth/login", { email, password });
      const accessToken = res?.access_token ?? res?.token;
      if (typeof accessToken === "string" && accessToken.length > 0) {
        ls.set("token", accessToken, { encrypt: true });
        try {
          localStorage.setItem("token", accessToken);
        } catch {
          /* quota / private mode — Bearer still in ls when decrypt works */
        }
      }
      if (typeof res?.refresh_token === "string" && res.refresh_token.length > 0) {
        ls.set("refresh_token", res.refresh_token, { encrypt: true });
      }
      if (res?.expires_in != null) {
        ls.set("expires_in", String(res.expires_in), { encrypt: true });
      }

      if ((res as any)?.message) {
        // Some backends return a success message only
        toast.success(String((res as any).message));
      } else {
        toast.success("Signed in successfully!");
      }

      // Refresh /me to populate local user profile + store.
      await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
      navigate(redirectPath, { replace: true });
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message;
      toast.error(typeof msg === "string" && msg.length > 0 ? msg : "Sign in failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (hasAppLoggedOutFlag()) {
    return <AuthTransitionScreen />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <header className="h-16 flex items-center justify-between px-6 border-b border-slate-200/60 bg-white/70 backdrop-blur dark:border-slate-800/70 dark:bg-slate-900/40">
        <div className="flex items-center gap-3">
          <SenrysaLogo className="p-1" />

        </div>
        <div className="flex items-center gap-2">
          {/* reserved for future header actions (theme/apps) */}
        </div>
      </header>

      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl shadow-emerald-900/5 dark:shadow-slate-900/50 border border-slate-200/60 dark:border-slate-700/60 p-8">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Bizeye</h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Sign in to your account to continue
              </p>
            </div>

            <form className="w-full space-y-4" onSubmit={onSubmit}>
            <div className="mb-1">
              <label htmlFor="email" className="text-sm font-medium mb-1 block text-gray-700 dark:text-slate-300">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full h-10 px-3 rounded-lg border-2 bg-white dark:bg-slate-700 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-slate-100 focus:border-emerald-600 dark:focus:border-emerald-500 focus:ring-2 focus:ring-emerald-600 dark:focus:ring-emerald-500 focus:ring-opacity-50 focus:outline-none placeholder-gray-400 dark:placeholder-slate-500"
                placeholder="Enter your email"
              />
            </div>

            <div className="mb-1">
              <div className="flex justify-between items-center mb-1">
                <label htmlFor="password" className="text-sm font-medium text-gray-700 dark:text-slate-300">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="text-xs text-emerald-600 dark:text-blue-400 hover:text-emerald-700 dark:hover:text-blue-300 transition-colors"
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full h-10 px-3 rounded-lg border-2 bg-white dark:bg-slate-700 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-slate-100 focus:border-emerald-600 dark:focus:border-emerald-500 focus:ring-2 focus:ring-emerald-600 dark:focus:ring-emerald-500 focus:ring-opacity-50 focus:outline-none placeholder-gray-400 dark:placeholder-slate-500"
                placeholder="Enter your password"
              />
            </div>

            <div className="flex items-center justify-between mb-1 gap-2">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-3 w-3 rounded border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-emerald-600 focus:ring-emerald-500"
                />
                <label htmlFor="remember-me" className="ml-1 block text-xs text-gray-700 dark:text-slate-300">
                  Remember me
                </label>
              </div>
              <Link
                to="/forgot-password"
                className="text-xs font-medium text-emerald-600 dark:text-blue-400 hover:text-emerald-700 dark:hover:text-blue-300 whitespace-nowrap"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full p-3 rounded-lg text-white font-medium transition-all duration-200 bg-emerald-600 hover:bg-emerald-700 dark:bg-blue-600 dark:hover:bg-blue-500 focus:ring-2 focus:ring-emerald-500 dark:focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-800 disabled:opacity-70"
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </button>
          </form>

            <p className="text-center text-sm text-gray-600 dark:text-slate-400 mt-4">
              Don&apos;t have an account?{" "}
              <Link
                to="/register"
                className="text-emerald-600 dark:text-blue-400 hover:text-emerald-700 dark:hover:text-blue-300 font-medium"
              >
                Register
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

